import type { AppConfig, AppServices } from '../../interfaces/src';
import type { SupabaseClientOptions } from '@supabase/supabase-js';
import { SupabaseDataService } from './data-service';
import { SupabaseAuthService } from './auth-service';
import { SupabaseStorageService } from './storage-service';
import { FlouciPaymentService } from './flouci-payment-service';
import { getSupabaseClient } from './client';
import {
  isDurableMutationTransportAuthorized,
  resolveDurableMutationTenant,
} from './durable-mutation-transport';

export function createSupabaseServices(config: AppConfig): AppServices {
  // Storage follows the data layer: uploads land on the tenant when one is
  // provisioned, otherwise on the master (the "everything in master by default"
  // mode). Auth always lives on master.
  const storageUrl = config.tenantUrl ?? config.masterUrl;
  const storageKey = config.tenantAnonKey ?? config.masterAnonKey;
  // Force the master client to be created with the custom session storage
  // BEFORE the auth service touches it — getSupabaseClient memoises by URL,
  // so the first call wins. This is the seam that makes desktop sessions
  // land in the OS keychain instead of localStorage.
  //
  // Skip when URL/key are empty (no .env yet) — the underlying services
  // already defer the createClient call until a real call is made, so we
  // shouldn't throw "supabaseUrl is required" at boot just to prime the
  // cache.
  const authOptions = config.disableAuthPersistence
    ? {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          detectSessionInUrl: false,
        },
      }
    : config.sessionStorage
      ? {
          auth: {
            // Supabase forwards these directly to GoTrueClient. Async
            // getItem is honoured by gotrue-js as long as we set
            // persistSession: true (the default).
            storage: config.sessionStorage as Storage,
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
          },
        }
      : undefined;
  const clientOptions = withTenantContext(
    authOptions,
    config.adminTenantId,
    config.adminAccessToken,
    config.getActiveTenantId,
    config.getDurableMutationIdentity,
  );

  if (clientOptions && config.masterUrl && config.masterAnonKey) {
    getSupabaseClient(config.masterUrl, config.masterAnonKey, clientOptions);
  }
  return {
    dataService: new SupabaseDataService(
      config.masterUrl,
      config.masterAnonKey,
      config.tenantUrl,
      config.tenantAnonKey,
      clientOptions,
    ),
    authService: new SupabaseAuthService(
      config.masterUrl,
      config.masterAnonKey,
      clientOptions,
      config.adminAccessToken,
    ),
    storageService: new SupabaseStorageService(storageUrl, storageKey, clientOptions),
    // Flouci one-shot payments via edge functions. When the Flouci tokens
    // aren't configured server-side the function answers 503
    // payment_not_configured, which surfaces as the same typed error the
    // old stub threw — the UI's "coming soon" path still works.
    paymentService: new FlouciPaymentService(
      config.masterUrl,
      config.masterAnonKey,
      clientOptions,
    ),
  };
}

/** Deux contextes de tenant peuvent voyager en en-tête, et ils ne se
 *  mélangent pas :
 *
 *  - `x-lyfty-admin-tenant-view` — excursion d'un ADMIN PLATEFORME dans le
 *    tenant d'un client. Le serveur ne l'honore que pour un admin.
 *  - `x-lyfty-tenant`            — espace de travail actif d'un utilisateur
 *    membre de plusieurs sociétés. Le serveur ne l'honore que s'il existe
 *    une ligne `tenant_members` correspondante.
 *
 *  Quand la vue admin est active elle GAGNE : `current_user_tenant_id()`
 *  teste `platform_admin_requested_tenant_id()` en premier. On n'envoie donc
 *  pas l'espace actif dans ce cas — ce serait un en-tête mort qui brouille
 *  la lecture des logs PostgREST.
 *
 *  Différence de traitement volontaire entre les deux : la vue admin part en
 *  en-tête STATIQUE (elle s'accompagne d'un autre jeton `Authorization`, donc
 *  elle DOIT avoir son propre client), l'espace actif est injecté par le
 *  `fetch` À CHAQUE REQUÊTE. Voir `AppConfig.getActiveTenantId` : en en-tête
 *  statique, chaque bascule fabriquerait une instance GoTrue de plus. */
export function withTenantContext(
  options: SupabaseClientOptions<'public'> | undefined,
  adminTenantId: string | undefined,
  adminAccessToken: string | undefined,
  getActiveTenantId: (() => string | undefined) | undefined,
  getDurableMutationIdentity:
    | (() => { userId: string; tenantId: string } | undefined)
    | undefined,
): SupabaseClientOptions<'public'> | undefined {
  if (
    !adminTenantId &&
    !adminAccessToken &&
    !getActiveTenantId &&
    !getDurableMutationIdentity
  ) {
    return options;
  }

  const base: SupabaseClientOptions<'public'> = {
    ...(options ?? {}),
    global: {
      ...(options?.global ?? {}),
      headers: {
        ...(options?.global?.headers ?? {}),
        ...(adminAccessToken ? { Authorization: `Bearer ${adminAccessToken}` } : {}),
        ...(adminTenantId ? { 'x-lyfty-admin-tenant-view': adminTenantId } : {}),
      },
    },
  };

  // La vue admin l'emporte : pas d'en-tête d'espace actif dans ce mode.
  //
  // Keep the header in `global.headers` as the client's immutable identity
  // (and cache key), but also attach it at the transport boundary. A tenant
  // view must never silently turn into the operator's default workspace just
  // because a Supabase internal request rebuilt its headers. The server is
  // still the authority: it accepts this header only for a full platform admin.
  if (adminTenantId) {
    const upstreamFetch = options?.global?.fetch;
    return {
      ...base,
      global: {
        ...base.global,
        fetch: ((...args: Parameters<typeof fetch>) => {
          const [input, init] = args;
          const headers = new Headers(
            typeof Request !== 'undefined' && input instanceof Request
              ? input.headers
              : undefined,
          );
          new Headers(init?.headers).forEach((value, key) => {
            headers.set(key, value);
          });
          headers.set('x-lyfty-admin-tenant-view', adminTenantId);
          if (adminAccessToken) {
            headers.set('Authorization', `Bearer ${adminAccessToken}`);
          }
          return (upstreamFetch ?? globalThis.fetch)(input, { ...init, headers });
        }) as typeof fetch,
      },
    };
  }

  if (!getActiveTenantId) return base;

  const upstreamFetch = options?.global?.fetch;
  return {
    ...base,
    global: {
      ...base.global,
      // Cast bridges the DOM and React Native overload sets for `fetch`; the
      // implementation accepts the union used by both runtimes.
      fetch: ((...args: Parameters<typeof fetch>) => {
        const [input, init] = args;
        const storedTenantId = getActiveTenantId();
        const headers = new Headers(
          typeof Request !== 'undefined' && input instanceof Request
            ? input.headers
            : undefined,
        );
        new Headers(init?.headers).forEach((value, key) => {
          headers.set(key, value);
        });
        const mutationIdentity = getDurableMutationIdentity?.();
        const tenantId = resolveDurableMutationTenant(
          storedTenantId,
          mutationIdentity,
        );
        if (
          mutationIdentity &&
          !isDurableMutationTransportAuthorized({
            request: input,
            headers,
            activeTenantId: tenantId,
            expected: mutationIdentity,
          })
        ) {
          return Promise.reject(
            new Error('durable_mutation_auth_identity_changed'),
          );
        }
        // Pas d'espace actif choisi (cas mono-société, l'immense majorité) →
        // on ne touche à rien et la requête part telle quelle.
        if (!tenantId) {
          return (upstreamFetch ?? globalThis.fetch)(input, {
            ...init,
            headers,
          });
        }
        headers.set('x-lyfty-tenant', tenantId);
        return (upstreamFetch ?? globalThis.fetch)(input, { ...init, headers });
      }) as typeof fetch,
    },
  };
}
